import { LoadingButton } from "@mui/lab";
import {
  ChipProps,
  SxProps,
  Theme,
  darken,
  lighten,
  useTheme,
} from "@mui/material";
import React, { CSSProperties } from "react";

export interface ModalActionButtonProps {
  onClick?: (e: any) => void;
  children?: React.ReactNode;
  variant?: "primary" | "secondary" | "outlined";
  disabled?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  size?: ChipProps["size"];
  style?: CSSProperties;
  sx?: SxProps;
  endIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  [key: string]: any;
}

export function ModalActionButtonV2(props: ModalActionButtonProps) {
  const theme = useTheme();
  const styles = useStyles(theme, props);
  const {
    onClick,
    children,
    variant,
    disabled,
    isDisabled,
    isLoading,
    size,
    style,
    sx,
    endIcon,
    startIcon,
    ...restProps
  } = props;

  return (
    <LoadingButton
      size={size || "large"}
      onClick={onClick}
      disabled={disabled || isDisabled}
      loading={isLoading}
      startIcon={startIcon}
      endIcon={endIcon}
      sx={{
        ...styles.root,
        ...styles[(props?.variant || "primary") as keyof typeof styles],
        ...style,
        ...sx,
      }}
      {...restProps}
    >
      {props?.children}
    </LoadingButton>
  );
}

function useStyles(
  theme: Theme,
  { variant, disabled, isDisabled, isLoading }: Partial<ModalActionButtonProps>
) {
  const primaryColor = theme.palette.primary.main;
  const secondaryColor = "#d6d6d6";
  const loadingFontColor = "rgba(0, 0, 0, 0)";

  return {
    root: {
      minWidth: "120px",
      cursor: "pointer",
      borderRadius: "50px",
      fontWeight: "600",
      border: "none",
      opacity: disabled || isDisabled ? 0.5 : 1,
      fontSize: "95%",
      px: 2,
    },
    primary: {
      backgroundColor: primaryColor,
      color: !isLoading
        ? `${theme.palette.common.white} !important`
        : loadingFontColor,
      border: `1px solid ${primaryColor}`,

      "& .MuiCircularProgress-root": {
        color: theme.palette.common.white,
      },

      "&:hover": {
        backgroundColor: lighten(primaryColor, 0.2),
        outline: `4px solid ${lighten(primaryColor, 0.7)}`,
        border: `1px solid ${lighten(primaryColor, 0.2)}`,
      },
    },
    secondary: {
      backgroundColor: secondaryColor,
      color: !isLoading
        ? `${theme.palette.text.primary} !important`
        : loadingFontColor,
      border: `1px solid ${secondaryColor}`,

      "& .MuiCircularProgress-root": {
        color: theme.palette.text.primary,
      },
      "&:hover": {
        backgroundColor: lighten(secondaryColor, 0.1),
        outline: `4px solid #ebebeb`,
      },
    },
    outlined: {
      backgroundColor: theme.palette.grey[50],
      color: !isLoading
        ? `${darken(theme.palette.primary.main, 0.3)} !important`
        : loadingFontColor,
      border: `1px solid ${theme.palette.primary.main}`,

      "& .MuiCircularProgress-root": {
        color: darken(theme.palette.primary.main, 0.3),
      },
      "&:hover": {
        backgroundColor: lighten(theme.palette.common.white, 0.1),
        outline: `4px solid ${lighten(theme.palette.primary.main, 0.7)}`,
      },
    },
  };
}
